// src/components/dashboard/tabs/DashboardAPITab.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert,
    Divider
} from '@mui/material';
import {
    Code,
    Plus,
    Eye,
    EyeOff,
    Copy,
    RefreshCw,
    Trash2,
    Check,
    ExternalLink
} from 'lucide-react';
import {
    fetchApiClients,
    createApiClient,
    regenerateApiClientSecret,
    deleteApiClient,
    fetchSubscriptionStatus
} from '../../../services/api';
import config from '../../../config';

const DashboardAPITab = ({  token, setError }) => {
    const { t } = useTranslation();

    // State for API clients
    const [apiClients, setApiClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // State for subscription status
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

    // State for dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // State for secret visibility
    const [visibleSecrets, setVisibleSecrets] = useState({});

    // State for copy confirmation
    const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
    const [copiedText, setCopiedText] = useState('');

    // State for operations
    const [isCreating, setIsCreating] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Load API clients and subscription status on mount
    useEffect(() => {
        loadApiClients();
        loadSubscriptionStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Function to load subscription status
    const loadSubscriptionStatus = async () => {
        setIsLoadingSubscription(true);
        try {
            const response = await fetchSubscriptionStatus(token);
            setSubscriptionStatus(response);
        } catch (err) {
            setError && setError(t('subscription.error.loading'));
            
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    // Function to load API clients
    const loadApiClients = async () => {
        setIsLoading(true);
        try {
            const response = await fetchApiClients(token);
            const data = Array.isArray(response) ? response : [];
            setApiClients(data);
        } catch (err) {
            setError && setError(t('dashboard.api.error.loading_clients'));
            
        } finally {
            setIsLoading(false);
        }
    };

    // Handler for creating a new API client
    const handleCreateClient = async () => {
        if (!newClientName.trim()) {
            return;
        }

        setIsCreating(true);
        try {
            const response = await createApiClient(token, newClientName);
            setApiClients(prev => [...prev, response]);
            setCreateDialogOpen(false);
            setNewClientName('');

            // Automatically show the secret for the new client
            setVisibleSecrets(prev => ({
                ...prev,
                [response.id]: true
            }));
        } catch (err) {
            setError && setError(t('dashboard.api.error.creating_client'));
            
        } finally {
            setIsCreating(false);
        }
    };

    // Handler for regenerating client secret
    const handleRegenerateSecret = async (clientId) => {
        setIsRegenerating(clientId);
        try {
            const response = await regenerateApiClientSecret(token, clientId);
            setApiClients(prev =>
                prev.map(client =>
                    client.id === clientId ? response : client
                )
            );

            // Automatically show the new secret
            setVisibleSecrets(prev => ({
                ...prev,
                [clientId]: true
            }));
        } catch (err) {
            setError && setError(t('dashboard.api.error.regenerating_secret'));
            
        } finally {
            setIsRegenerating(null);
        }
    };

    // Handler for deleting a client
    const handleDeleteClient = async () => {
        if (!selectedClient) return;

        setIsDeleting(true);
        try {
            await deleteApiClient(token, selectedClient.id);
            setApiClients(prev => prev.filter(client => client.id !== selectedClient.id));
            setDeleteDialogOpen(false);
            setSelectedClient(null);
        } catch (err) {
            setError && setError(t('dashboard.api.error.deleting_client'));
            
        } finally {
            setIsDeleting(false);
        }
    };

    // Handler for toggling secret visibility
    const toggleSecretVisibility = (clientId) => {
        setVisibleSecrets(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    // Handler for copying text to clipboard
    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedText(label);
            setCopySnackbarOpen(true);
        });
    };

    return (
        <div className="space-y-8">
            {/* Header with title and create button */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <Code className="text-indigo-500 w-6 h-6" />
                    <Typography variant="h5">{t('dashboard.api.title')}</Typography>
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setCreateDialogOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}
                >
                    {t('dashboard.api.create_key')}
                </Button>
            </div>

            {/* API Keys section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-200">
                    <Typography variant="h6" className="font-medium">
                        {t('dashboard.api.keys')}
                    </Typography>
                </div>

                <div className="p-6">
                    {isLoading ? (
                        <div className="flex justify-center my-8">
                            <CircularProgress />
                        </div>
                    ) : apiClients.length === 0 ? (
                        <div className="text-center my-8 text-gray-500">
                            <Typography>{t('dashboard.api.no_keys')}</Typography>
                            <Button
                                variant="outlined"
                                className="mt-4"
                                onClick={() => setCreateDialogOpen(true)}
                                color="primary"
                            >
                                {t('dashboard.api.create_first_key')}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {apiClients.map(client => (
                                <div key={client.id} className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <Typography variant="h6" className="font-medium">
                                                {client.name}
                                            </Typography>
                                            <Typography variant="body2" className="text-gray-500 mt-1">
                                                {t('dashboard.api.created_at')}: {new Date(client.created_at).toLocaleDateString()}
                                            </Typography>
                                            <div className="mt-2">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    client.is_active
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {client.is_active
                                                        ? t('dashboard.api.status.active')
                                                        : t('dashboard.api.status.inactive')
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <Tooltip title={t('dashboard.api.delete')}>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedClient(client);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <Divider className="my-4" />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <Typography variant="subtitle2" className="font-medium mb-2 text-gray-700">
                                                {t('dashboard.api.client_id')}
                                            </Typography>
                                            <div className="flex items-center gap-2">
                                                <div className="bg-white py-2 px-3 rounded border border-gray-200 font-mono text-sm flex-grow overflow-x-auto">
                                                    {client.client_id}
                                                </div>
                                                <Tooltip title={t('dashboard.api.copy')}>
                                                    <IconButton
                                                        size="small"
                                                        className="text-blue-600"
                                                        onClick={() => handleCopyToClipboard(client.client_id, t('dashboard.api.client_id'))}
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <Typography variant="subtitle2" className="font-medium text-gray-700">
                                                    {t('dashboard.api.client_secret')}
                                                </Typography>
                                                <Tooltip title={t('dashboard.api.regenerate')}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            className="text-blue-600"
                                                            disabled={isRegenerating === client.id}
                                                            onClick={() => handleRegenerateSecret(client.id)}
                                                        >
                                                            {isRegenerating === client.id ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <RefreshCw className="w-4 h-4" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="bg-white py-2 px-3 rounded border border-gray-200 font-mono text-sm flex-grow overflow-x-auto">
                                                    {visibleSecrets[client.id]
                                                        ? client.client_secret
                                                        : client.client_secret.substring(0, 5) + ''.padEnd(client.client_secret.length - 5, '*')}
                                                </div>
                                                <Tooltip title={visibleSecrets[client.id] ? t('dashboard.api.hide') : t('dashboard.api.show')}>
                                                    <IconButton
                                                        size="small"
                                                        className="text-blue-600"
                                                        onClick={() => toggleSecretVisibility(client.id)}
                                                    >
                                                        {visibleSecrets[client.id] ? (
                                                            <EyeOff className="w-4 h-4" />
                                                        ) : (
                                                            <Eye className="w-4 h-4" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('dashboard.api.copy')}>
                                                    <IconButton
                                                        size="small"
                                                        className="text-blue-600"
                                                        onClick={() => handleCopyToClipboard(client.client_secret, t('dashboard.api.client_secret'))}
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Documentation section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-200">
                    <Typography variant="h6" className="font-medium">
                        {t('dashboard.api.documentation.title')}
                    </Typography>
                </div>

                <div className="p-6 space-y-8">
                    {/* Example usage section */}
                    <div>
                        <Typography variant="subtitle1" className="font-medium mb-3">
                            {t('dashboard.api.documentation.usage_examples')}
                        </Typography>
                        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                            <pre className="text-sm font-mono">
{`# Example: Authentication
curl -X POST \\
  ${config.clientApiUrl}/auth/token/ \\
  -H 'Content-Type: application/json' \\
  -d '{
    "username": "your_username",
    "password": "your_password"
  }'

# Response:
# {
#   "access": "your_jwt_access_token",
#   "refresh": "your_jwt_refresh_token"
# }

# Example: List sandboxes
curl -X GET \\
  ${config.clientApiUrl}/sandboxes/ \\
  -H 'Authorization: Bearer your_jwt_access_token'`}
                            </pre>
                        </div>
                    </div>

                    {/* Available endpoints section */}
                    <div>
                        <Typography variant="subtitle1" className="font-medium mb-3">
                            {t('dashboard.api.documentation.available_endpoints')}
                        </Typography>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('dashboard.api.documentation.method')}
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('dashboard.api.documentation.endpoint')}
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('dashboard.api.documentation.description')}
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                POST
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/auth/token/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.auth_token')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                POST
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/auth/token/refresh/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.refresh_token')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                GET
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/sandboxes/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.list_sandboxes')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                GET
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/sandboxes/{'{id}'}/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.sandbox_details')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                POST
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/sandbox/process/start/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.start_process')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                GET
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                        /client-api/v1/tutors/
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {t('dashboard.api.documentation.list_tutors')}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 text-right">
                            <Button
                                variant="text"
                                color="primary"
                                component="a"
                                href="/client-api/schema/"
                                target="_blank"
                                endIcon={<ExternalLink className="w-4 h-4" />}
                                className="text-indigo-600 hover:text-indigo-700"
                            >
                                {t('dashboard.api.documentation.view_full_docs')}
                            </Button>
                        </div>
                    </div>

                    {/* API Limits section */}
                    <div>
                        <Typography variant="subtitle1" className="font-medium mb-3">
                            {t('dashboard.api.documentation.api_limits')}
                        </Typography>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <Typography variant="body2" className="text-gray-600 mb-3">
                                {t('dashboard.api.documentation.rate_limit_description')}
                            </Typography>

                            {isLoadingSubscription ? (
                                <div className="flex justify-center py-4">
                                    <CircularProgress size={24} />
                                </div>
                            ) : subscriptionStatus ? (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Typography variant="body2" className="font-medium">
                                            {t('subscription.resources.api_requests')}:
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Typography variant="body2" className="font-medium text-indigo-600">
                                                {subscriptionStatus?.limits?.api_requests ? (
                                                    subscriptionStatus.limits.api_requests.max > 0 ?
                                                        `${subscriptionStatus.limits.api_requests.used} / ${subscriptionStatus.limits.api_requests.max} ${t('dashboard.api.documentation.requests_per_day')}` :
                                                        t('subscription.unlimited')
                                                ) : (
                                                    subscriptionStatus?.plan_type === 'free' ?
                                                        `0 / 100 ${t('dashboard.api.documentation.requests_per_day')}` :
                                                        t('subscription.unlimited')
                                                )}
                                            </Typography>
                                        </div>
                                    </div>

                                    {subscriptionStatus?.limits?.api_requests && subscriptionStatus.limits.api_requests.max > 0 && (
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full ${
                                                    (subscriptionStatus.limits.api_requests.used / subscriptionStatus.limits.api_requests.max) > 0.9 ? 'bg-red-500' : 
                                                    (subscriptionStatus.limits.api_requests.used / subscriptionStatus.limits.api_requests.max) > 0.7 ? 'bg-yellow-500' : 
                                                    'bg-green-500'
                                                }`}
                                                style={{ width: `${Math.min(100, Math.round((subscriptionStatus.limits.api_requests.used / subscriptionStatus.limits.api_requests.max) * 100))}%` }}
                                            ></div>
                                        </div>
                                    )}

                                    <Typography variant="body2" className="text-gray-600 mt-2">
                                        {t('dashboard.api.documentation.limit_note')}
                                    </Typography>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-green-600">
                                    <Check className="w-5 h-5" />
                                    <Typography variant="body2" className="font-medium">
                                        {t('dashboard.api.documentation.standard_limit', { count: 60 })}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create API Client Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => !isCreating && setCreateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('dashboard.api.create_key_dialog.title')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('dashboard.api.create_key_dialog.name')}
                        fullWidth
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        disabled={isCreating}
                    />
                    <Typography variant="body2" className="mt-4 text-gray-500">
                        {t('dashboard.api.create_key_dialog.description')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateDialogOpen(false)}
                        disabled={isCreating}
                        color="inherit"
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleCreateClient}
                        variant="contained"
                        color="primary"
                        disabled={!newClientName.trim() || isCreating}
                        startIcon={isCreating ? <CircularProgress size={16} /> : null}
                        className="bg-indigo-600 hover:bg-indigo-700"
                    >
                        {t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="text-red-600">
                    {t('dashboard.api.delete_dialog.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        {t('dashboard.api.delete_dialog.confirmation', { name: selectedClient?.name })}
                    </Typography>
                    <Typography variant="body2" className="mt-4 text-red-500 font-medium">
                        {t('dashboard.api.delete_dialog.warning')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeleting}
                        color="inherit"
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteClient}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} /> : null}
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Snackbar */}
            <Snackbar
                open={copySnackbarOpen}
                autoHideDuration={3000}
                onClose={() => setCopySnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setCopySnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {t('dashboard.api.copy_success', { field: copiedText })}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default DashboardAPITab;
